/**
 * HeroStatStrip
 *
 * Top-of-page summary showing today's key numbers at a glance.
 * Pulls from existing Redux state (no extra API calls needed).
 */
/* eslint-disable eqeqeq */
import React from 'react';
import { useSelector } from 'react-redux';
import { Box, Skeleton, Typography } from '@mui/material';
import { FRESH_WINDOW_DAYS } from './BriefingPanel';

const Stat = ({ label, value, hint, accent, loading }) => (
  <Box
    sx={{
      flex: 1,
      minWidth: { xs: '50%', md: 'auto' },
      px: { xs: 2, md: 3 },
      py: { xs: 2, md: 2.5 },
      borderRight: { xs: 'none', md: '1px solid var(--dwlf-ink-06)' },
      borderBottom: { xs: '1px solid var(--dwlf-ink-06)', md: 'none' },
      '&:last-child': {
        borderRight: 'none',
        borderBottom: 'none',
      },
    }}
  >
    <Typography
      sx={{
        fontFamily: '"IBM Plex Mono", monospace',
        fontSize: '0.6rem',
        letterSpacing: '0.18em',
        textTransform: 'uppercase',
        color: 'var(--dwlf-ink-40)',
        mb: 0.75,
      }}
    >
      {label}
    </Typography>
    {loading ? (
      <>
        <Skeleton
          variant="text"
          width={60}
          sx={{ fontSize: '2rem', bgcolor: 'var(--dwlf-ink-06)' }}
        />
        <Skeleton
          variant="text"
          width={100}
          sx={{ fontSize: '0.7rem', bgcolor: 'var(--dwlf-ink-04)', mt: 0.5 }}
        />
      </>
    ) : (
      <>
        <Typography
          sx={{
            fontFamily: '"Space Grotesk", sans-serif',
            fontSize: { xs: '1.6rem', md: '2rem' },
            fontWeight: 500,
            color: accent || 'var(--dwlf-text)',
            letterSpacing: '-0.02em',
            lineHeight: 1.1,
          }}
        >
          {value}
        </Typography>
        {hint && (
          <Typography
            sx={{
              fontFamily: '"IBM Plex Mono", monospace',
              fontSize: '0.7rem',
              color: 'var(--dwlf-ink-45)',
              mt: 0.5,
            }}
          >
            {hint}
          </Typography>
        )}
      </>
    )}
  </Box>
);

const HeroStatStrip = () => {
  const watchlist = useSelector((s) => s.markets.watchlist || []);
  const watchlistLoading = useSelector((s) => !!s.markets.watchlistLoading);
  const briefingData = useSelector((s) => s.markets.briefingData);
  const briefingLoading = useSelector((s) => !!s.markets.briefingLoading);
  const performance = useSelector((s) => s.markets.performanceSummary);
  const performanceLoading = useSelector((s) => !!s.markets.performanceSummaryLoading);

  // Count *custom event fires* across the user's watchlist over the
  // shared "fresh" window. The briefing payload exposes per-symbol
  // `activeEvents` arrays — user-configured event fires (alarms the
  // user set up that triggered). Pull the window length from
  // FRESH_WINDOW_DAYS so this stays in lockstep with the Briefing
  // badges' "NEW" window and the custom event chip window in
  // BriefingPanel — if that constant ever changes, this updates
  // automatically.
  const windowStart = Date.now() - FRESH_WINDOW_DAYS * 24 * 60 * 60 * 1000;
  let customEventFires = 0;
  if (briefingData?.symbols) {
    Object.values(briefingData.symbols).forEach((symbolData) => {
      (symbolData?.activeEvents || []).forEach((e) => {
        const t = new Date(e.firedAt || e.date || 0).getTime();
        if (Number.isFinite(t) && t >= windowStart) {
          customEventFires += 1;
        }
      });
    });
  }

  // Pull best/worst from performance summary.
  // Shape (per PerformanceWidget): performanceSummary.ytdChange.{best,worst}
  // = { symbol, change, name }
  const bestYTD = performance?.ytdChange?.best;
  const worstYTD = performance?.ytdChange?.worst;

  return (
    <Box
      sx={{
        backgroundColor: 'var(--dwlf-surface)',
        border: '1px solid var(--dwlf-ink-08)',
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        flexWrap: { xs: 'wrap', md: 'nowrap' },
      }}
    >
      <Stat
        label="Watching"
        value={watchlist.length}
        hint={watchlist.length === 1 ? 'symbol on your watchlist' : 'symbols on your watchlist'}
        loading={watchlistLoading}
      />
      <Stat
        label={`Custom events · ${FRESH_WINDOW_DAYS}d`}
        value={customEventFires}
        hint={customEventFires === 1 ? 'fire on your watchlist' : 'fires on your watchlist'}
        accent={customEventFires > 0 ? 'var(--dwlf-accent)' : undefined}
        loading={briefingLoading}
      />
      <Stat
        label="Best YTD"
        value={
          bestYTD?.change != null
            ? `${bestYTD.change >= 0 ? '+' : ''}${bestYTD.change.toFixed(1)}%`
            : '—'
        }
        hint={bestYTD?.symbol || 'across all tracked'}
        accent="#3fb950"
        loading={performanceLoading}
      />
      <Stat
        label="Worst YTD"
        value={
          worstYTD?.change != null
            ? `${worstYTD.change.toFixed(1)}%`
            : '—'
        }
        hint={worstYTD?.symbol || 'across all tracked'}
        accent="#f85149"
        loading={performanceLoading}
      />
    </Box>
  );
};

export default HeroStatStrip;
